'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useCreatePromptPresetMutation } from '../hooks/queries/usePromptPresets';
import { UserConventionSelector, type UserConventionOption } from './UserConventionSelector';

type SavePromptAsPresetButtonProps = {
  token: string | null;
  projectId: string;
  prompt: object;
  userConventionIds?: string[];
  userConventionOptions?: UserConventionOption[];
  onSaved?: () => void;
  /** 프리셋 쓰기 가능 여부. false면 버튼을 그리지 않는다(판정은 서버, 이 값은 안내용). */
  canWrite?: boolean;
};

export const SavePromptAsPresetButton = ({
  token,
  projectId,
  prompt,
  userConventionIds = [],
  userConventionOptions = [],
  onSaved,
  canWrite = true,
}: SavePromptAsPresetButtonProps) => {
  const t = useTranslations('components.appLayout');
  const tCommon = useTranslations('common');
  const createMutation = useCreatePromptPresetMutation(token, projectId);

  const [isOpen, setIsOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [selectedUserConventionIds, setSelectedUserConventionIds] = useState<string[]>(userConventionIds);
  const [isShared, setIsShared] = useState(false);

  const resetForm = () => {
    setPresetName('');
    setSelectedUserConventionIds(userConventionIds);
    setIsShared(false);
  };

  const handleUserConventionToggle = (conventionId: string, checked: boolean) => {
    setSelectedUserConventionIds((prev) => {
      if (checked) {
        if (prev.includes(conventionId)) return prev;
        return [...prev, conventionId];
      }
      return prev.filter((id) => id !== conventionId);
    });
  };

  const handleSave = async () => {
    if (!presetName.trim()) return;

    try {
      await createMutation.mutateAsync({
        name: presetName.trim(),
        prompt,
        userConventionIds: selectedUserConventionIds,
        isShared,
      });
      toast.success(t('promptPresetSaved'));
      resetForm();
      setIsOpen(false);
      onSaved?.();
    } catch {
      toast.error(t('promptPresetSaveFailed'));
    }
  };

  if (!canWrite) return null;

  if (!isOpen) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedUserConventionIds(userConventionIds);
          setIsOpen(true);
        }}
      >
        {t('promptPresetSaveFromList')}
      </button>
    );
  }

  return (
    <div
      className="mt-2 w-full rounded-lg border border-base-300 bg-base-200 p-3 space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        className="input input-bordered input-sm w-full"
        placeholder={t('promptPresetNamePlaceholder')}
        value={presetName}
        onChange={(e) => setPresetName(e.target.value)}
        maxLength={100}
        autoFocus
      />
      {userConventionOptions.length > 0 ? (
        <UserConventionSelector
          label={t('userConventionsLabel')}
          emptyHint={t('userConventionsEmpty')}
          conventions={userConventionOptions}
          selectedIds={selectedUserConventionIds}
          onToggle={handleUserConventionToggle}
          maxHeightClassName="max-h-32"
        />
      ) : null}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-xs checkbox-primary"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
          />
          <span className="text-xs text-[var(--color-fg-muted)]">{t('promptPresetShared')}</span>
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => {
              setIsOpen(false);
              resetForm();
            }}
          >
            {tCommon('actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-xs"
            onClick={handleSave}
            disabled={!presetName.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? t('promptPresetSaving') : t('promptPresetSave')}
          </button>
        </div>
      </div>
    </div>
  );
};
